import * as Yup from 'yup';
import moment from 'moment';
import { useMutation } from '@tanstack/react-query';
import { updateClientInstallation } from '@api/services/client-installation.service';
import { IBusinessDevelopmentUser, IClientInstallation } from '@api/models';
import { ClientInstallationStatusEnum } from '@api/enum/client-installation.enum';

type TClientInstallationFormValues = {
    name: string;
    poc: { name: string; email: string; phoneNumber: number | string; designation?: string }[];

    location: {
        country: string;
        latitude: string;
        longitude: string;
        googleMapsLink: string;
    };
    contract: {
        startDate: string;
        endDate: string;
    };
    businessDevelopmentUser?: string;
    files: File[];
    businessDevelopmentUserName?: string;
    status: ClientInstallationStatusEnum;
};

const useClientInstallationUpdateForm = (initialData: IClientInstallation) => {
    const schema = Yup.object({
        name: Yup.string().required('Name is required'),

        poc: Yup.array().of(
            Yup.object({
                name: Yup.string().required('Name is required'),
                email: Yup.string().email('Invalid email').required('Email is required'),
                phoneNumber: Yup.number(),
            })
        ),

        location: Yup.object({
            country: Yup.string().required('Country is required'),
            latitude: Yup.string(),
            longitude: Yup.string(),
            googleMapsLink: Yup.string().url('Enter a valid URL'),
        }),

        contract: Yup.object({
            startDate: Yup.string()
                .nullable()
                .test('valid-startDate', 'Invalid start date', (value) => {
                    if (!value) return true; // allow empty string or null
                    return moment(value, 'YYYYMMDD', true).isValid();
                }),

            endDate: Yup.string()
                .nullable()
                .test('valid-endDate', 'Invalid end date', (value) => {
                    if (!value) return true; // allow empty string or null
                    return moment(value, 'YYYYMMDD', true).isValid();
                })
                .test('end-after-start', 'End date must be same or after start date', function (endDate) {
                    const { startDate } = this.parent;

                    if (!startDate || !endDate) return true; // Allow empty or null start or end date
                    const start = moment(startDate, 'YYYYMMDD', true);
                    const end = moment(endDate, 'YYYYMMDD', true);

                    // Ensuring the end date is same or after the start date
                    return end.isSameOrAfter(start, 'day');
                }),
        }),

        businessDevelopmentUser: Yup.string().required('This field is required'),

        files: Yup.array().of(Yup.mixed()),
    });

    const initialValues: TClientInstallationFormValues = {
        name: initialData.name || '',
        poc: initialData.poc || [{ name: '', email: '', phoneNumber: '', designation: '' }],

        location: {
            country: initialData.location?.country || '',
            latitude: initialData.location?.latitude || '',
            longitude: initialData.location?.longitude || '',
            googleMapsLink: initialData.location?.googleMapsLink || '',
        },

        contract: {
            startDate: initialData.contract?.startDate ? moment(initialData.contract.startDate).format('YYYYMMDD') : '',
            endDate: initialData.contract?.endDate ? moment(initialData.contract.endDate).format('YYYYMMDD') : '',
        },

        businessDevelopmentUser: (initialData.businessDevelopmentUser as IBusinessDevelopmentUser)._id,
        status: initialData.status,
        files: [],
    };

    const updateMutation = useMutation((payload: any) => updateClientInstallation(initialData._id, payload));

    const submit = async (values: TClientInstallationFormValues) => {
        const payload = {
            name: values.name,
            businessDevelopmentUser: values.businessDevelopmentUser || '',
            poc: values?.poc.map((poc) => ({
                name: poc.name,
                email: poc.email,
                phoneNumber: poc.phoneNumber.toString(),
                designation: poc.designation || '',
            })),
            location: {
                country: values.location.country,
                latitude: values.location.latitude,
                longitude: values.location.longitude,
                googleMapsLink: values.location.googleMapsLink,
            },
            contract: {
                startDate: values.contract.startDate,
                endDate: values.contract.endDate,
            },
            status: values.status,
        };
        // if (moment(payload.contract.endDate).isBefore(moment(payload.contract.startDate))) {
        //     throw new Error('End date must be same or after start date');
        // }

        const data = await updateMutation.mutateAsync(payload); // Send the JSON payload instead of FormData
        return data;
    };

    return {
        initialValues,
        schema,
        isSubmitting: updateMutation.isLoading,
        submit,
    };
};

export default useClientInstallationUpdateForm;
